import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { initTheme } from '../utils/theme';

export const AuthContext = createContext(null);
export const API = process.env.REACT_APP_API_URL || '/api';

// Global 401 interceptor: if a non-auth request gets a 401, the token expired.
// Clear it and redirect to the camp's login page (or root if unknown).
axios.interceptors.response.use(
  res => res,
  err => {
    if (
      err.response?.status === 401 &&
      !err.config?.url?.includes('/auth/')
    ) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      const parts = window.location.pathname.split('/').filter(Boolean);
      // URL shape: /:campSlug/intern/...
      if (parts.length >= 2 && parts[1] === 'intern') {
        // Preserve current URL so CampLoginPage can redirect back after login
        sessionStorage.setItem('loginRedirect', window.location.pathname);
        window.location.href = `/${parts[0]}/login`;
      }
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user,          setUser]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [lang,          setLangState]     = useState(() => {
    const l = localStorage.getItem('lang') || 'de';
    document.documentElement.lang = l;
    return l;
  });
  const [icons,         setIcons]         = useState({});
  const [settings,      setSettings]      = useState({ site_title:'Baseball Camp', site_subtitle:'CAMP', site_tagline:'' });
  const [simulatedUser, setSimulatedUser] = useState(null);

  const loadIcons = () =>
    axios.get(`${API}/users/icons`).then(r => {
      const map = {}; r.data.forEach(i => { map[i.key] = i.value; }); setIcons(map);
    }).catch(() => {});

  const loadSettings = () =>
    axios.get(`${API}/settings`).then(r => { setSettings(r.data); initTheme(r.data); }).catch(() => {});

  useEffect(() => {
    loadIcons();
    loadSettings();
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(r => setUser(r.data))
        .catch(() => { localStorage.removeItem('token'); delete axios.defaults.headers.common['Authorization']; })
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  // Update browser tab title and favicon whenever settings change
  useEffect(() => {
    if (settings.site_title) {
      document.title = settings.site_tagline || settings.site_title;
    }
  }, [settings.site_title, settings.site_tagline]);

  useEffect(() => {
    // Update favicon dynamically
    const faviconUrl = settings.favicon_url;
    const faviconEmoji = settings.favicon_emoji || '⚾';
    const link = document.getElementById('favicon') || document.querySelector("link[rel~='icon']");
    if (!link) return;
    if (faviconUrl) {
      link.href = faviconUrl + '?t=' + Date.now();
      link.type = faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
    } else {
      // Emoji favicon via canvas
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      const ctx = canvas.getContext('2d');
      ctx.font = '52px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(faviconEmoji, 32, 36);
      link.href = canvas.toDataURL('image/png');
      link.type = 'image/png';
    }
  }, [settings.favicon_url, settings.favicon_emoji]);

  const setLang = (l) => { localStorage.setItem('lang', l); document.documentElement.lang = l; setLangState(l); };

  const login = async (username, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { username, password });
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshUser = async () => {
    try { const r = await axios.get(`${API}/auth/me`); setUser(r.data); } catch(e) {}
  };

  const hasRole = (...roles) => {
    if (!user) return false;
    const userRoles = user.roles || [user.role] || [];
    return roles.some(r => userRoles.includes(r));
  };

  const canEdit            = () => hasRole('admin','social_media');
  const isAdmin            = () => hasRole('admin');
  const isCoach            = () => hasRole('admin','coach');
  const isShowMaster       = () => hasRole('admin','show_master');
  const canManageTournament= () => hasRole('admin','tournament_manager');
  const canManageQuestions = () => hasRole('admin','tournament_manager','show_master');

  // Simulation mode
  const simulateUser = async (campSlug, userId) => {
    const { data } = await axios.get(`${API}/${campSlug}/roles/simulate/${userId}`);
    setSimulatedUser(data);
  };
  const exitSimulation = () => setSimulatedUser(null);

  // Permission check — uses simulated user's resolved permissions when simulating
  const hasPermission = (key) => {
    if (simulatedUser) return simulatedUser.effectivePermissions?.[key] === true;
    if (!user) return false;
    if (user.isSuperadmin) return true;
    // Fallback for pages not yet migrated to permission keys
    return hasRole('admin');
  };

  const icon         = (key, fallback='?') => icons[key] || fallback;
  const refreshIcons = () => loadIcons();
  const refreshSettings = () => loadSettings();

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading,
      hasRole, canEdit, isAdmin, isCoach, isShowMaster, canManageTournament, canManageQuestions,
      simulatedUser, simulateUser, exitSimulation, hasPermission, isSimulating: !!simulatedUser,
      API, lang, setLang,
      icon, icons, refreshIcons,
      settings, refreshSettings,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
